import { inject, Injectable } from '@angular/core';
import { Navigation } from 'app/core/navigation/navigation.types';
import { Observable, ReplaySubject, skip, tap } from 'rxjs';
import {cloneDeep} from "lodash-es";
import {
    defaultNavigation,
} from "../../nagivation/navigation-tree";
import { AuthService } from 'app/core/auth/password-grant/auth.service';

@Injectable({providedIn: 'root'})
export class NavigationService
{
    private _navigation: ReplaySubject<Navigation> = new ReplaySubject<Navigation>(1);
    private auth: AuthService = inject(AuthService);

    constructor() {
        this.auth.isAuthenticated$()
            .pipe(skip(1))
            .subscribe(() => {
                this.get().subscribe();
            });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for navigation
     */
    get navigation$(): Observable<Navigation>
    {
        return this._navigation.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all navigation data
     */
    get(): Observable<Navigation>
    {
        return new Observable<Navigation>((observer) => {
            observer.next({
                compact   : cloneDeep(defaultNavigation),
                default   : cloneDeep(defaultNavigation),
                futuristic: cloneDeep(defaultNavigation),
                horizontal: cloneDeep(defaultNavigation)
            });

            observer.complete();
        }).pipe(
            tap((navigation) => {
                const navigationFiltered = this.hideNavigation(navigation);
                this._navigation.next(navigationFiltered);
            })
        );
    }

    private hideNavigation(navigation): Navigation {
        navigation.default = navigation.default.filter(obj => this.filterParent(obj));
        navigation.futuristic = navigation.futuristic.filter(obj => this.filterParent(obj));
        navigation.horizontal = navigation.horizontal.filter(obj => this.filterParent(obj));
        navigation.compact = this.filterCompact(navigation.compact);

        return navigation;
    }

    private filterParent(obj): boolean {
        if (obj.id === 'gestao-usuarios' && this.auth.isSelfServiceProfile()) {
            return false;
        }

        if(obj.children) {
            const filteredChildren = this.filterObj(obj);
            obj.children = filteredChildren;
            return filteredChildren.length > 0;
        } else {
            return this.canAccessItem(obj);
        }
    }

    private filterObj(obj): any {
        return obj.children.filter((objChildren) => {
            if (objChildren.children) {
                const filteredChildren = this.filterObj(objChildren);
                objChildren.children = filteredChildren;
                return filteredChildren.length > 0;
            } else {
                return this.canAccessItem(objChildren);
            }
        });
    }

    private filterCompact(obj): any {
        const filteredCompact = cloneDeep(obj);
        let noneRemoved = true;

        for(let i = 0; i < obj.length; i++) {
            const compactObj = filteredCompact.find(filt => filt.id === obj[i].id);
            const index = filteredCompact.indexOf(compactObj, 0);

            if(obj[i].type === 'group' && (!obj[i+1] || (obj[i+1] && obj[i+1].type === 'divider'))) {
                if (index > -1) {
                    filteredCompact.splice(index, 1);
                }
                noneRemoved = false;
            }

            if(obj[i].type === 'divider' && ((!obj[i+1]) || (obj[i+1] && obj[i+1].type === 'divider'))) {
                if (index > -1) {
                    filteredCompact.splice(index, 1);
                }
                noneRemoved = false;
            }

            if(!this.canAccessItem(obj[i])) {
                if (index > -1) {
                    filteredCompact.splice(index, 1);
                }
                noneRemoved = false;
            }
        }

        if (noneRemoved) {
            return filteredCompact;
        } else {
            return this.filterCompact(filteredCompact);
        }
    }

    private canAccessItem(obj): boolean {
        return this.auth.hasAccess({
            roles: obj.roles,
            permissions: obj.permission
        });
    }
}
